import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permission } from '@wms/shared';
import { apiFetch, ApiError } from './api';

export interface SessionProfile {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
  readonly locale: string;
  readonly permissions: string[];
  readonly warehouseId?: string;
  readonly mustChangePassword: boolean;
  readonly hasOverridePin: boolean;
}

export const SESSION_QUERY_KEY = ['session', 'me'];

/**
 * 当前会话查询。401 时静默返回 null（未登录是正常状态，不是错误）——
 * 路由守卫据此决定是否跳转登录页，而不是把每一次未登录都当异常上报。
 */
export function useSession() {
  const query = useQuery<SessionProfile | null>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiFetch<SessionProfile>('/session/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 30_000,
    retry: false,
  });

  /**
   * 【用户实测 2026-08-17】会话 cookie 是整个浏览器共用的——在另一个标签页
   * 登录别的账号会替换本标签页的会话。旧标签页界面还挂着旧身份，请求却已
   * 全部以新身份执行，处处 403，看起来像"账号崩了"。检测到身份切换（焦点
   * 回来时 refetchOnWindowFocus 会重拉 session/me）就整页刷新，让标签页
   * 干净地以新身份重建。退出登录（session 变 null）仍由 RequireAuth 处理。
   */
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (query.data === undefined) return; // 尚未取到，不建立基准
    const nextId = query.data?.userId ?? null;
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = nextId;
    if (prev !== undefined && prev !== null && nextId !== null && prev !== nextId) {
      window.location.reload();
    }
  }, [query.data]);

  return query;
}

/**
 * 【实现教训】必须用 `refetchQueries`（等新数据回来）而不是
 * `invalidateQueries`（只标记过期，后台异步刷新，函数本身立刻返回）。
 * 登录页调用 `await invalidateSession()` 之后立刻 `navigate()` 到受保护路由，
 * `RequireAuth` 那一刻读到的 `data` 还是刷新前的旧值（未登录时是 `null`）——
 * `isPending` 在这种"有旧数据、正在后台重新拉取"的场景下是 `false`，不会让
 * 守卫等待，于是刚登录成功却被立刻弹回登录页。`refetchQueries` 会等请求真正
 * 完成、`data` 变成最新值之后才 resolve，调用方这时候再 navigate 才安全。
 */
export function useInvalidateSession() {
  const queryClient = useQueryClient();
  return () => queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY });
}

/**
 * useCan() —— 只影响渲染，绝不是安全边界（架构书 §11.2、Spec §43）。
 * 真正的强制点永远在后端 Guard。
 */
export function useCan(permission: Permission): boolean {
  const { data } = useSession();
  return data?.permissions.includes(permission) ?? false;
}
